import { Routes, Route, Navigate } from "react-router-dom";
import { ErrorBoundary } from "@hub/ui/components/ErrorBoundary";
import { ProtectedRoute } from "@hub/auth";
import { WorkbenchLayout } from "./core/components/WorkbenchLayout";
import { AuthRoutes } from "./modules/auth/routes";
import { QueueRoutes } from "./modules/queue/routes";
import { WorkItemRoutes } from "./modules/workitem/routes";
import { DashboardRoutes } from "./modules/dashboard/routes";
import { ShellPreview } from "./shell/ShellPreview";
import { WorkbenchShellRoute } from "./shell/WorkbenchShellRoute";
import { AuditDocWindow } from "./audit/AuditDocWindow";

function App() {
  // Backendless-preview robustness (HUB-504). The app root (`/`) and any unmatched
  // path fall through to the protected index below (→ /queue → ProtectedRoute →
  // /login). With no auth backend wired — the local dev / preview harness Doug
  // walks the audit workbench in — that sinks the user to the sign-in screen on a
  // bare-root open, a bookmark to the base, or a dev-server-restart reload that
  // resolves to `/workbench/` rather than the `/shell-preview` deep link. The audit
  // blade flow itself never navigates the URL, so this is a routing/harness gap, not
  // an audit regression. In dev, land root + unknown paths on the seeded backendless
  // preview so it is never lost to login; production (import.meta.env.DEV === false)
  // keeps the real protected index — auth on /w, /queue, /dashboard is untouched.
  const isDev = import.meta.env.DEV;
  return (
    <ErrorBoundary>
      <Routes>
        <Route path="/login/*" element={<AuthRoutes />} />

        {/* Phase-2 blade-canvas shell preview (HUB-275) — public, no backend needed. */}
        <Route path="/shell-preview" element={<ShellPreview />} />

        {/* Popped-out anesthesia-record document window (HUB-504). Opened by the
            compare screen's "Pop out" action; renders the styled document viewer in
            its own window. The URL carries only the blind case id — never MRN,
            name, or any payload field.

            PROTECTED, like every other view that reads real data.

            It used to be public, on the reasoning recorded here that it was
            "self-contained (mock data), no auth". That was true of the
            backendless preview and became wrong the moment the workbench went
            live (HUB-512): this window resolves the case through the SAME
            evalreview.v1 service as the compare screen, which needs a token.

            The failure was not a permission error, which is why it read as a
            data problem. window.open creates a new JS realm, so the access
            token — deliberately in-memory only, never localStorage (see
            @hub/api client.ts) — starts null there. AuthProvider does restore
            the session from the httpOnly cookie, but it renders its children
            WHILE that request is in flight rather than gating on it; only
            ProtectedRoute waits. So this window fired getCase before it had a
            token, the call failed, and an undefined case yields an empty
            documentBlobId — which the pane renders, correctly for what it was
            given, as "no document for this case". A reviewer who believes the
            chart is missing judges the case on the codes alone, and that is a
            corrupted datapoint in a study measuring coding judgement.

            ProtectedRoute holds the render until the session resolves, which is
            what every other live view already relies on.

            EXCEPT in the backendless dev preview, on the same `isDev` axis the
            index route above already turns on — and for the same reason. There
            is no auth backend there, so wrapping this unconditionally sent the
            preview's "Pop out" to a sign-in form inside a 940px popup, which is
            the walkthrough Doug demos the review tool with. The original "mock
            data, no auth" reasoning was never wrong about the preview; it was
            wrong about production. This scopes it to where it holds. */}
        <Route
          path="/audit-doc"
          element={
            isDev ? (
              <AuditDocWindow />
            ) : (
              <ProtectedRoute>
                <AuditDocWindow />
              </ProtectedRoute>
            )
          }
        />

        {/* App root. In prod → the blade shell at /w, which is the front door
            (HUB-509 pilot). It used to be /queue, the older WorkbenchLayout
            route; that split meant an auditor signing in landed on a
            reviewer's assignment-scoped queue and saw an empty page, while the
            evaluation queue they DO have access to sat one unadvertised URL
            away. /queue stays mounted for existing links.

            In the backendless dev/preview harness → the seeded shell preview,
            so a reload or bare-root open stays in the preview instead of
            bouncing to /login. */}
        <Route
          index
          element={<Navigate to={isDev ? "/shell-preview" : "/w"} replace />}
        />

        {/* Deep-linkable, live-data blade-canvas shell (HUB-275 + HUB-277). Auth-
            gated (its primitives bind real @hub/api entities), but rendered as its
            own full-screen shell — NOT inside WorkbenchLayout (it has its own
            chrome). The seeded, backendless preview stays at /shell-preview. */}
        <Route
          path="/w/*"
          element={
            <ProtectedRoute>
              <WorkbenchShellRoute />
            </ProtectedRoute>
          }
        />

        <Route
          element={
            <ProtectedRoute>
              <WorkbenchLayout />
            </ProtectedRoute>
          }
        >
          <Route path="queue" element={<QueueRoutes />} />
          <Route path="queue/:id" element={<WorkItemRoutes />} />
          <Route path="dashboard/*" element={<DashboardRoutes />} />
        </Route>

        {/* Dev/preview harness only: keep an unknown path out of /login by recovering
            into the backendless preview. Prod routing is left unchanged. */}
        {isDev && <Route path="*" element={<Navigate to="/shell-preview" replace />} />}
      </Routes>
    </ErrorBoundary>
  );
}


export default App;
